import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import type { LeaveTypesRepositoryT } from "../domain/ports/leaveTypes.repository";
import type { PolicyRepositoryT } from "shared/domain/ports/policy.repository";
import type { LeaveTypeEntityT } from "../domain/entities/leaveType.entity";
import type { LeaveTypeUiT } from "../ui/models/leaveType.model";
import type {
  CreateLeaveTypeFormValuesT,
  UpdateLeaveTypeTypeFormValuesT,
} from "../domain/types/leaveType.types";
import type { PermissionPolicyFormValuesT } from "shared/domain/types/policy.types";

import { APPROVAL_FLOW_VALUES } from "../constants/approvalFlow";

import { getLeaveTypes } from "../application/useCases/getLeaveTypes";
import { createLeaveType } from "../application/useCases/createLeaveType";
import { updateLeaveType } from "../application/useCases/updateLeaveType";
import { deleteLeaveTypes } from "../application/useCases/deleteLeaveTypes";
import { toggleLeaveTypeActiveStatus } from "../application/useCases/toggleLeaveTypeActiveStatus";

import {
  getBalanceControlValue,
  getFormattedDaysValue,
  getFutureDatesValue,
  getPastDatesValue,
  getResetMonth,
} from "./helpers";

const toUiModel = (leaveTypes: LeaveTypeEntityT[]): LeaveTypeUiT[] =>
  leaveTypes.map((item) => {
    const isPolicyActive = item.policy?.active;

    return {
      id: item.id,
      active: item.active ?? false,
      code: item.code ?? null,
      name: item.name ?? null,
      daysPerRequest: isPolicyActive
        ? getFormattedDaysValue(item.policy?.maxDaysPerRequest)
        : "-",
      daysPerYear: isPolicyActive
        ? getFormattedDaysValue(item.policy?.annualDays)
        : "-",
      incidence: item.incidence?.name ?? null,
      isCustom: item.isCustom ?? null,
      balanceControl: isPolicyActive
        ? getBalanceControlValue(item.policy?.hasBalanceControl)
        : "-",
      icon: isPolicyActive ? (item.icon ?? null) : null,
      approval: isPolicyActive
        ? (APPROVAL_FLOW_VALUES[
            item.policy?.approvalFlow as keyof typeof APPROVAL_FLOW_VALUES
          ] ?? null)
        : "-",
      annualReset: isPolicyActive
        ? getResetMonth(item.policy?.resetMonth)
        : "-",
      futureDates: isPolicyActive
        ? getFutureDatesValue(item.policy?.minAdvanceNoticeDays)
        : "-",
      pastDates: isPolicyActive
        ? getPastDatesValue(item.policy?.maxBackdateDays)
        : "-",
      minAdvanceNoticeDays: item.policy?.minAdvanceNoticeDays ?? null,
      maxBackdateDays: item.policy?.maxBackdateDays ?? null,
    };
  });

const removeDeletedTypes = (
  oldData: LeaveTypeEntityT[] | undefined,
  succeeded: string[],
) => {
  if (!oldData) return oldData;
  return oldData.filter((type) => !succeeded.includes(type.id));
};

const applyActiveStatusToggle = (
  oldData: LeaveTypeEntityT[] | undefined,
  typeId: string,
  isActive: boolean,
) => {
  if (!oldData) return oldData;

  return oldData.map((type) =>
    type.id === typeId ? { ...type, active: isActive } : type,
  );
};

type CreateUseLeaveTypesParamsT = {
  leaveTypesRepository: LeaveTypesRepositoryT;
  policyRepository: PolicyRepositoryT;
};

export const createUseLeaveTypes = ({
  leaveTypesRepository,
  policyRepository,
}: CreateUseLeaveTypesParamsT) => {
  return function useLeaveTypes({
    colaboraInstanceId,
  }: {
    colaboraInstanceId?: string;
  }) {
    const queryClient = useQueryClient();

    const leaveTypesQuery = useQuery({
      queryKey: ["vacations/leave-types", { colaboraInstanceId }],
      queryFn: () =>
        getLeaveTypes({
          leaveTypesRepository,
          colaboraInstanceId: colaboraInstanceId,
        }),
      select: toUiModel,
      enabled: !!colaboraInstanceId,
    });

    const createLeaveTypeMutation = useMutation({
      mutationFn: ({ data }: { data: CreateLeaveTypeFormValuesT }) =>
        createLeaveType({
          leaveTypesRepository,
          data,
          colaboraInstanceId: colaboraInstanceId,
        }),
      onSuccess: () => {
        // Invalidate cached leave types after creating a new leave type,
        // because the mutation response does not include all required fields.
        queryClient.invalidateQueries({
          queryKey: ["vacations/leave-types", { colaboraInstanceId }],
        });
      },
    });

    const updateLeaveTypeMutation = useMutation({
      mutationFn: ({
        type,
        policy,
        policyId,
      }: {
        type: UpdateLeaveTypeTypeFormValuesT;
        policy: PermissionPolicyFormValuesT;
        policyId: string | null;
      }) =>
        updateLeaveType({
          leaveTypesRepository,
          policyRepository,
          type,
          policy,
          policyId,
          colaboraInstanceId: colaboraInstanceId,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["vacations/leave-types", { colaboraInstanceId }],
        });
      },
    });

    const deleteLeaveTypeMutation = useMutation({
      mutationFn: ({ typeIds }: { typeIds: string[] }) =>
        deleteLeaveTypes({
          leaveTypesRepository,
          typeIds,
          colaboraInstanceId: colaboraInstanceId,
        }),
      onSuccess: ({ succeeded }) => {
        if (succeeded.length === 0) return;

        queryClient.setQueryData(
          ["vacations/leave-types", { colaboraInstanceId }],
          (oldData: LeaveTypeEntityT[] | undefined) =>
            removeDeletedTypes(oldData, succeeded),
        );
      },
    });

    const queryKey = ["vacations/leave-types", { colaboraInstanceId }];

    const toggleLeaveTypeActiveStatusMutation = useMutation({
      mutationFn: ({
        typeId,
        isActive,
      }: {
        typeId: string;
        isActive: boolean;
      }) =>
        toggleLeaveTypeActiveStatus({
          leaveTypesRepository,
          typeId,
          isActive,
          colaboraInstanceId: colaboraInstanceId,
        }),
      onMutate: async ({ typeId, isActive }) => {
        await queryClient.cancelQueries({ queryKey });

        const previousData =
          queryClient.getQueryData<LeaveTypeEntityT[]>(queryKey);

        queryClient.setQueryData(
          queryKey,
          (oldData: LeaveTypeEntityT[] | undefined) =>
            applyActiveStatusToggle(oldData, typeId, isActive),
        );

        return { previousData };
      },
      onError: (
        _error: unknown,
        _variables: unknown,
        context: { previousData?: LeaveTypeEntityT[] } | undefined,
      ) => {
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
      },
    });

    return {
      ...leaveTypesQuery,
      createLeaveType: createLeaveTypeMutation,
      updateLeaveType: updateLeaveTypeMutation,
      deleteLeaveType: deleteLeaveTypeMutation,
      toggleLeaveTypeActiveStatus: toggleLeaveTypeActiveStatusMutation,
    };
  };
};
